import React, { useReducer, useRef, useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import {
    UserInputs,
    LocationsDropdownWrapper,
    consultSchema,
    reducer,
    NearbyLocations,
    ShowAddressOrLink,
    initialFormData,
    initialUserData,
    FORM_ACTIONS,
    FORM_PROGRESS,
} from '.'
import { NETLIFY_FUNCTIONS_PATH, getFacebookCookies } from '../../../utils'
import axios from 'axios'
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    ConsentMsg,
} from '../shared'
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts'

export function MultipleStores({ siteData }) {
    const [formState, dispatch] = useReducer(reducer, initialFormData)
    const isBrowser = typeof window !== 'undefined'
    const cloudampDataRef = useRef(null)

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false)
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null)
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 })
    const apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`

    //used to capture the cloudamp data value and store it in localStorage
    useEffect(() => {
        // Wait for 3 seconds before accessing the value
        const timer = setTimeout(() => {
            if (cloudampDataRef.current) {
                // Set the value of localStorage to the value cloudampDataRef.current.value
                localStorage.setItem(
                    'cloudamp__data__c',
                    cloudampDataRef.current.value
                )
            }
        }, 5000)
        // Cleanup timer on component unmount
        return () => clearTimeout(timer)
    }, [])

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('consult-form')
        helpers.setSubmitting(true)
        const { fbp, fbc } = getFacebookCookies()
        try {
            const formData = {
                ...values,
                city: formState.metro.name,
                state: formState.metro.stateAbbrev,
                location: formState.store.salesforceValue,
                consultType: formState.include.formAction,
                updates: true,
                cloudamp__data__c: cloudampDataRef.current.value,
                fbp,
                fbc,
            }

            // Store form values for use in attribution survey - only for Ask Question action
            setFormValues({ formType: 'question', ...formData })

            // Submit to Netlify function
            const response = await axios.post(apiEndpoint, formData)

            helpers.setSubmitting(false)
            helpers.setStatus('Success')

            // If backend returned a Self-Schedule URL, perform a top-level redirect
            if (
                isBrowser &&
                formState.include.formAction === FORM_ACTIONS.selfSchedule &&
                response.data.redirectUrl
            ) {
                helpers.resetForm()
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                })
                window.location.href = response.data.redirectUrl
                return
            } else {
                setShowAttributionSurvey(true)
                helpers.resetForm()
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                })
            }
        } catch (error) {
            console.error('Form submission error:', error)
            helpers.setSubmitting(false)
            helpers.setStatus('Failed')
            setFormValues(null)

            setTimeout(() => {
                helpers.resetForm()
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                })
                setShowAttributionSurvey(false)
            }, 5000)
        }
    }

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false)
        setTimeout(() => {
            setFormValues(null)
        }, 10000) // Clear form values after 10 second
    }

    return (
        <div id="consult-form" className="hr-divider hr-divider-consult">
            <span id="consult" className="anchor-tag"></span>
            <h2 className="text-center subheadsm">Book Your Free Consult</h2>
            <Formik
                initialValues={initialUserData}
                validationSchema={consultSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {
                    // This way we can get access to all formik props
                    formik => {
                        return (
                            <Form className="container" method="POST">
                                <input
                                    ref={cloudampDataRef}
                                    type="hidden"
                                    name="cloudamp__data__c"
                                />

                                {formik.isSubmitting && <FormSubmitting />}
                                {formik.status === 'Failed' && <FormFailed />}
                                {/* Only for Ask Question Action */}
                                {formValues &&
                                    !formik.isSubmitting &&
                                    formik.status !== 'Failed' && (
                                        <>
                                            {showAttributionSurvey ? (
                                                <div className="row justify-content-center">
                                                    <div className="col-12 col-xl-10">
                                                        <AttributionSurvey
                                                            formValues={
                                                                formValues
                                                            }
                                                            onComplete={
                                                                handleAttributionComplete
                                                            }
                                                            isFullWidth={false}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <FormSuccess />
                                            )}
                                        </>
                                    )}

                                {/* Original form content - only shown when not in special states */}
                                {!formik.isSubmitting &&
                                    formik.status !== 'Failed' &&
                                    !formValues && (
                                        <>
                                            <UserInputs
                                                isQuestion={
                                                    formState.include
                                                        .formAction ===
                                                    FORM_ACTIONS.question
                                                }
                                            />
                                            <div className="row locations-container">
                                                <div
                                                    className={`col locations-wrapper ${
                                                        formState.include
                                                            .formAction !==
                                                            '' &&
                                                        formState.include
                                                            .formProgress !==
                                                            FORM_PROGRESS.initiateQuestion
                                                            ? 'toggle'
                                                            : ''
                                                    }`}
                                                >
                                                    <NearbyLocations
                                                        currentMetro={
                                                            siteData.currentMetro
                                                        }
                                                        dispatch={dispatch}
                                                    />
                                                    <LocationsDropdownWrapper
                                                        {...{
                                                            formState,
                                                            dispatch,
                                                            siteData,
                                                        }}
                                                    />
                                                    <div className="row justify-content-center selected-location-container mb-3">
                                                        <div
                                                            className={`col-xl-10 selected-location-wrapper ${
                                                                formState.store
                                                                    .salesforceValue &&
                                                                formState.store
                                                                    .salesforceValue !==
                                                                    ''
                                                                    ? 'toggle'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <div className="mb-0 text-center">
                                                                {(formState
                                                                    .store
                                                                    .open ||
                                                                    formState
                                                                        .include
                                                                        .formAction ===
                                                                        FORM_ACTIONS.question) && (
                                                                    <ShowAddressOrLink
                                                                        formState={
                                                                            formState
                                                                        }
                                                                        currentMetro={
                                                                            siteData.currentMetro
                                                                        }
                                                                    />
                                                                )}
                                                                {!formState
                                                                    .store
                                                                    .open &&
                                                                    formState
                                                                        .include
                                                                        .formAction !==
                                                                        FORM_ACTIONS.question && (
                                                                        <div className="d-flex justify-content-center mb-3">
                                                                            <div
                                                                                className="alert alert-warning col text-center p-2 mb-0"
                                                                                role="alert"
                                                                            >
                                                                                Selected
                                                                                location
                                                                                is
                                                                                not
                                                                                open
                                                                                yet,
                                                                                but
                                                                                you
                                                                                can
                                                                                still
                                                                                <span
                                                                                    onClick={() =>
                                                                                        dispatch(
                                                                                            {
                                                                                                type: 'formAction',
                                                                                                formAction:
                                                                                                    FORM_ACTIONS.question,
                                                                                                formProgress:
                                                                                                    FORM_PROGRESS.initiateQuestion,
                                                                                                user: formik.values,
                                                                                            }
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    &nbsp;submit
                                                                                    a
                                                                                    question
                                                                                </span>

                                                                                !
                                                                                Or
                                                                                select
                                                                                a
                                                                                different
                                                                                location.
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {formState.include.formAction ===
                                                '' && (
                                                <div className="row justify-content-center mb-4">
                                                    <div className="col-xl-10 lh-1">
                                                        <ConsentMsg
                                                            cta={
                                                                'Ask A Question* or See Available Times**'
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div
                                                id="actions-btns"
                                                className="row gx-1 gy-4 justify-content-center"
                                            >
                                                <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                    {
                                                        // No button clicked yet / Initial setup
                                                        formState.include
                                                            .formAction ===
                                                            '' &&
                                                            formState.include
                                                                .formProgress ===
                                                                '' && (
                                                                <button
                                                                    type="button"
                                                                    className="milan-btn milan-navy-btn w-100"
                                                                    onClick={() =>
                                                                        dispatch(
                                                                            {
                                                                                type: 'formAction',
                                                                                formAction:
                                                                                    FORM_ACTIONS.question,
                                                                                formProgress:
                                                                                    FORM_PROGRESS.initiateQuestion,
                                                                                user: formik.values,
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    Ask A
                                                                    Question *
                                                                </button>
                                                            )
                                                    }
                                                    {
                                                        // First See Available Times Button clicked
                                                        formState.include
                                                            .formAction ===
                                                            FORM_ACTIONS.selfSchedule &&
                                                            formState.include
                                                                .formProgress ===
                                                                FORM_PROGRESS.selectLocation && (
                                                                <button
                                                                    type="button"
                                                                    className="milan-btn milan-fallback-btn w-100"
                                                                    onClick={() =>
                                                                        dispatch(
                                                                            {
                                                                                type: 'formAction',
                                                                                formAction:
                                                                                    '',
                                                                                formProgress:
                                                                                    '',
                                                                                user: formik.values,
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    Go Back
                                                                </button>
                                                            )
                                                    }
                                                    {formState.include
                                                        .formAction ===
                                                        FORM_ACTIONS.question && (
                                                        <>
                                                            {
                                                                // Ask A Question button clicked
                                                                formState
                                                                    .include
                                                                    .formProgress ===
                                                                    FORM_PROGRESS.initiateQuestion && (
                                                                    <button
                                                                        type="button"
                                                                        className="milan-btn milan-navy-btn w-100"
                                                                        onClick={() =>
                                                                            dispatch(
                                                                                {
                                                                                    type: 'formAction',
                                                                                    formAction:
                                                                                        FORM_ACTIONS.question,
                                                                                    formProgress:
                                                                                        FORM_PROGRESS.selectLocation,
                                                                                    user: formik.values,
                                                                                }
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !formik.isValid
                                                                        }
                                                                    >
                                                                        Next
                                                                    </button>
                                                                )
                                                            }
                                                            {
                                                                // Next button clicked
                                                                formState
                                                                    .include
                                                                    .formProgress ===
                                                                    FORM_PROGRESS.selectLocation && (
                                                                    <button
                                                                        className="milan-btn milan-navy-btn w-100"
                                                                        type="submit"
                                                                        disabled={
                                                                            !formik.isValid ||
                                                                            !formState
                                                                                .store
                                                                                .salesforceValue
                                                                        }
                                                                    >
                                                                        {formik.isSubmitting
                                                                            ? 'Submitting...'
                                                                            : 'Send Question'}
                                                                    </button>
                                                                )
                                                            }
                                                        </>
                                                    )}
                                                </div>

                                                <div className="d-none d-sm-block col-sm-2 col-lg-1 text-center">
                                                    <div className="my-3 my-md-2">
                                                        <strong>OR</strong>
                                                    </div>
                                                </div>

                                                <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                    {
                                                        // No button clicked yet / Initial setup
                                                        formState.include
                                                            .formAction ===
                                                            '' &&
                                                            formState.include
                                                                .formProgress ===
                                                                '' && (
                                                                <button
                                                                    type="button"
                                                                    className="milan-btn milan-red-btn w-100"
                                                                    onClick={() =>
                                                                        dispatch(
                                                                            {
                                                                                type: 'formAction',
                                                                                formAction:
                                                                                    FORM_ACTIONS.selfSchedule,
                                                                                formProgress:
                                                                                    FORM_PROGRESS.selectLocation,
                                                                                user: formik.values,
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    See
                                                                    Available
                                                                    Times **
                                                                </button>
                                                            )
                                                    }
                                                    {
                                                        // First See Available Times Button clicked
                                                        formState.include
                                                            .formAction ===
                                                            FORM_ACTIONS.selfSchedule &&
                                                            formState.include
                                                                .formProgress ===
                                                                FORM_PROGRESS.selectLocation && (
                                                                <button
                                                                    id="local-schedule-btn"
                                                                    type="submit"
                                                                    className="milan-btn milan-red-btn w-100"
                                                                    disabled={
                                                                        !formState
                                                                            .store
                                                                            .open ||
                                                                        !formik.isValid
                                                                            ? true
                                                                            : false
                                                                    }
                                                                >
                                                                    {formik.isSubmitting
                                                                        ? 'Submitting...'
                                                                        : 'See Available Times'}
                                                                </button>
                                                            )
                                                    }
                                                    {
                                                        // Form Action is Question
                                                        formState.include
                                                            .formAction ===
                                                            FORM_ACTIONS.question && (
                                                            <>
                                                                {
                                                                    // Ask A Question button clicked
                                                                    formState
                                                                        .include
                                                                        .formProgress ===
                                                                        FORM_PROGRESS.initiateQuestion && (
                                                                        <button
                                                                            type="button"
                                                                            className="milan-btn milan-fallback-btn w-100"
                                                                            onClick={() =>
                                                                                dispatch(
                                                                                    {
                                                                                        type: 'formAction',
                                                                                        formAction:
                                                                                            '',
                                                                                        formProgress:
                                                                                            '',
                                                                                        user: formik.values,
                                                                                    }
                                                                                )
                                                                            }
                                                                        >
                                                                            Go
                                                                            Back
                                                                        </button>
                                                                    )
                                                                }
                                                                {
                                                                    // Next button clicked
                                                                    formState
                                                                        .include
                                                                        .formProgress ===
                                                                        FORM_PROGRESS.selectLocation && (
                                                                        <button
                                                                            type="button"
                                                                            className="milan-btn milan-fallback-btn w-100"
                                                                            onClick={() =>
                                                                                dispatch(
                                                                                    {
                                                                                        type: 'formAction',
                                                                                        formAction:
                                                                                            FORM_ACTIONS.question,
                                                                                        formProgress:
                                                                                            FORM_PROGRESS.initiateQuestion,
                                                                                        user: formik.values,
                                                                                    }
                                                                                )
                                                                            }
                                                                        >
                                                                            Go
                                                                            Back
                                                                        </button>
                                                                    )
                                                                }
                                                            </>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </>
                                    )}
                            </Form>
                        )
                    }
                }
            </Formik>
        </div>
    )
}

export default MultipleStores
